import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Reveal from "@/components/base/Reveal";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { articleEn } from "@/mocks/pillarArticles/en";
import { articlePl } from "@/mocks/pillarArticles/pl";
import { articleEs } from "@/mocks/pillarArticles/es";
import { articleFr } from "@/mocks/pillarArticles/fr";
import type { ArticleData } from "@/mocks/pillarArticles/en";

const allByLang: Record<string, ArticleData[]> = {
  en: articleEn,
  pl: articlePl,
  es: articleEs,
  fr: articleFr,
};

interface FaqItem {
  q: string;
  a: string;
}

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const lang = i18n.language.split("-")[0];
  const articles = allByLang[lang] ?? allByLang.en;

  const article = useMemo(() => {
    if (!slug) return articles[0];
    return articles.find((a) => a.slug === slug) ?? allByLang.en.find((a) => a.slug === slug) ?? articles[0];
  }, [slug, articles]);

  // Scroll to top whenever slug or lang changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug, lang]);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faq.map((f: FaqItem) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "USFansLink",
    },
    datePublished: "2026-08-01",
    dateModified: "2026-08-22",
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <span className="text-foreground-700 line-clamp-1">{article.title}</span>
          </nav>

          <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl leading-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-6 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-primary-500"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              {article.published}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500"></i>
              {article.readingTime}
            </span>
          </div>

          <p className="text-foreground-700 text-base md:text-lg leading-relaxed mt-8">
            {article.intro}
          </p>

          <div className="mt-8 space-y-8">
            {article.sections.map((s) => (
              <Reveal key={s.heading}>
                <section>
                  <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                    {s.heading}
                  </h2>
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className="text-foreground-700 leading-relaxed mb-4">
                      {p}
                    </p>
                  ))}
                </section>
              </Reveal>
            ))}
          </div>

          {/* Comparison table */}
          <Reveal>
            <section className="mt-10 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-5">
                {article.table.heading}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {article.table.headers.map((h) => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200 whitespace-nowrap bg-background-100"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {article.table.rows.map((row, i) => (
                      <tr key={i} className={`border-b border-background-200/60 last:border-0 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100/40"}`}>
                        {row.map((cell, j) => (
                          <td
                            key={j}
                            className={`px-4 py-3 ${
                              j === 0 ? "font-medium text-foreground-900" : "text-foreground-600"
                            } align-top`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </Reveal>

          {/* FAQ */}
          <Reveal>
            <section className="mt-10">
              <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-6">
                FAQ
              </h2>
              <div className="space-y-3">
                {article.faq.map((f: FaqItem) => (
                  <details
                    key={f.q}
                    className="rounded-lg bg-background-50 border border-background-200 overflow-hidden group"
                  >
                    <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer list-none text-foreground-900 font-medium text-sm md:text-base">
                      {f.q}
                      <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0">
                        <i className="ri-add-line group-open:hidden"></i>
                        <i className="ri-subtract-line hidden group-open:inline"></i>
                      </span>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          </Reveal>

          {/* Conclusion CTA */}
          <Reveal>
            <div className="mt-10 rounded-lg bg-primary-50 border border-primary-200 p-6">
              <p className="text-foreground-800 leading-relaxed">{article.conclusion}</p>
              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-secondary-500 text-background-50 font-semibold text-sm hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("backHome")}
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("exploreSpreadsheet")}
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </Reveal>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}